import { useRef } from 'react';

// Components
import Input from '../../Input/Input';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './TextField.module.css';

/**
 * BatchActionTextInput - Text input component for batch actions
 * @param {object} props
 * @param {string | number} [props.value] - Current value
 * @param {(value: string | number) => void} props.onChange - Change handler
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} [props.onKeyDown] - Key down handler
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.name] - Input name
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.required] - Whether input is required
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} BatchActionTextInput component
 */
function TextField({
  value = '',
  onChange,
  placeholder,
  name,
  disabled = false,
  required = false,
  error = null,
  onBlur,
  onKeyDown,
  className = '',
}) {
  const inputRef = useRef(null);
  /**
   * Handle input change
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e
   */
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  /**
   * Get input props
   */
  const inputProps = {
    value: value || '',
    onChange: handleChange,
    placeholder,
    name,
    disabled,
    required,
    hasError: Boolean(error),
    className: cn([styles.input, className]),
    onBlur,
    onKeyDown,
  };

  return (
    <div className={cn([styles.wrapper, error && styles['has-error'], className])}>
      <Input
        ref={inputRef}
        theme="light"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
      />

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default TextField;
